import { EventBus, EVENTS } from '../../../../shared/EventBus';

/** Per-character discovered feature quests: `{ [characterOwner]: questId[] }`. A display hint only —
 *  discovery never accepts a quest, grants anything or reaches the server. */
const STORAGE_KEY = 'h2k_discovered_feature_quests:v2';
/** The first store was one global list shared by every character on the browser. It cannot be
 *  attributed to anyone, so it is dropped; chapters are re-recorded the next time an NPC shows them. */
const LEGACY_KEY = 'h2k_discovered_feature_quests';

export class DiscoveredQuestsStoreImpl {
  private byOwner = new Map<string, Set<string>>();
  private initialized = false;

  private init(): void {
    if (this.initialized) return;
    this.initialized = true;
    try {
      if (typeof localStorage === 'undefined') return;
      localStorage.removeItem(LEGACY_KEY);
      const saved = localStorage.getItem(STORAGE_KEY);
      const parsed: unknown = saved ? JSON.parse(saved) : null;
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return;
      for (const [owner, ids] of Object.entries(parsed as Record<string, unknown>)) {
        if (!owner || !Array.isArray(ids)) continue;
        this.byOwner.set(owner, new Set(ids.filter((id): id is string => typeof id === 'string' && id.length > 0)));
      }
    } catch {
      // LocalStorage unavailable in test/sandbox
    }
  }

  private persist(): void {
    try {
      if (typeof localStorage === 'undefined') return;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(
        [...this.byOwner].map(([owner, ids]) => [owner, [...ids]]))));
    } catch {
      // Storage full or unavailable: the in-memory set still serves this session.
    }
  }

  /** Chapters this character has been shown; empty without an owner. */
  getDiscoveredIds(owner: string): string[] {
    this.init();
    return owner ? [...(this.byOwner.get(owner) ?? [])] : [];
  }

  /** Record chapters an NPC showed this character. No owner, no record. */
  recordDiscovered(owner: string, questIds: readonly string[]): boolean {
    this.init();
    if (!owner) return false;
    const ids = this.byOwner.get(owner) ?? new Set<string>();
    let changed = false;
    for (const id of questIds) {
      if (id && !ids.has(id)) { ids.add(id); changed = true; }
    }
    if (!changed) return false;
    this.byOwner.set(owner, ids);
    this.persist();
    EventBus.getInstance().emit(EVENTS.QUEST_DISCOVERED_CHANGED, [...ids]);
    return true;
  }

  reset(): void {
    this.byOwner.clear();
    this.initialized = true;
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(LEGACY_KEY);
      }
    } catch {
      // nothing to clear
    }
    EventBus.getInstance().emit(EVENTS.QUEST_DISCOVERED_CHANGED, []);
  }
}

export const DiscoveredQuestsStore = new DiscoveredQuestsStoreImpl();
